import React from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { getSeriesColor } from "features/analytics/studio/utils/chartColors";
import { formatNumber } from "shared/utils/numberFormatter";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";

/** The Traces table chrome (dataTableStyles): one bordered card, hairline rows, no header fill. */
const tableContainerClass =
  "mt-4 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface";
const tableStaticRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

// Fallback palette when no series colors
const CHART_COLORS = [
  theme.accentChart,
  theme.primaryLight,
  "#8b5cf6",
  "#10b981",
  "var(--feedback-warning)",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

export default function InsightsDataTable({
  series,
  metricName,
  average,
  allSeries,
  seriesColors,
  breakdownProperties,
  breakdownFormat,
  chartType,
  fullTotalsByMetric,
}) {
  const isTableChart = String(chartType || "").toLowerCase().trim() === "table";

  // Table chart + breakdown: GroupedFlatTable (one row per deepest combo, no tree)
  if (isTableChart && breakdownFormat && allSeries?.length > 0 && allSeries[0]?.breakdownValues?.length > 0) {
    return (
      <GroupedFlatTable
        allSeries={allSeries}
        seriesColors={seriesColors}
        breakdownProperties={breakdownProperties}
        fullTotalsByMetric={fullTotalsByMetric}
      />
    );
  }

  // Non-table chart: hierarchical breakdown mode (row-span for dimension columns)
  if (breakdownFormat && allSeries?.length > 0 && allSeries[0]?.breakdownValues?.length > 0) {
    return (
      <HierarchicalTable
        allSeries={allSeries}
        seriesColors={seriesColors}
        breakdownProperties={breakdownProperties}
      />
    );
  }
  // Multi-series mode (flat)
  if (allSeries && Array.isArray(allSeries) && allSeries.length > 0) {
    return <MultiSeriesTable allSeries={allSeries} seriesColors={seriesColors} />;
  }
  // Legacy single series mode
  if (!series || !Array.isArray(series) || series.length === 0) {
    return null;
  }
  return <SingleSeriesTable series={series} metricName={metricName} average={average} seriesColors={seriesColors} />;
}

function SingleSeriesTable({ series, metricName, average, seriesColors }) {
  const color = (seriesColors && seriesColors.length > 0) ? seriesColors[0] : theme.accentChart;

  // Format date for display (e.g., "Aug 17", "Sep 9")
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      // Fallback: show last 5 chars if date parsing fails
      return dateStr.slice(-5);
    }
  };

  // Format date for header (e.g., "Aug 17" -> "Aug 17")
  const formatDateHeader = (dateStr) => {
    return formatDateForTable(dateStr);
  };
  return (
    <div className={tableContainerClass}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={tableHeaderRowClass}>
              <th className={tableHeaderCellClass}>
                Metric
              </th>
              <th className={`${tableHeaderCellClass} text-right`}>
                Average
              </th>
              {series.map((item, index) => (
                <th
                  key={index}
                  className={`${tableHeaderCellClass} text-right min-w-[72px]`}
                >
                  {formatDateHeader(item.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className={tableStaticRowClass}>
              <td className={tableDataCellClass}>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <span>{metricName || "Metric"}</span>
                </div>
              </td>
              <td className={`${tableDataCellClass} text-right tabular-nums`}>
                {average != null ? formatNumber(average, 1) : "0"}
              </td>
              {series.map((item, index) => (
                <td key={index} className={`${tableDataCellClass} text-right tabular-nums`}>
                  {item.value != null ? formatNumber(item.value) : "0"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MultiSeriesTable({ allSeries, seriesColors }) {
  // Get all unique dates across all series
  const dateSet = new Set();
  allSeries.forEach(series => {
    (series.data || []).forEach(point => {
      if (point.date) dateSet.add(point.date);
    });
  });
  const dates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

  // Format date for display (e.g., "Aug 17", "Sep 9")
  const formatDateForTable = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const day = date.getDate();
      return `${month} ${day}`;
    } catch (e) {
      return dateStr.slice(-5);
    }
  };

  // Create a map for quick lookup: date -> { metricId -> value }
  const dateValueMap = new Map();
  dates.forEach(date => {
    dateValueMap.set(date, new Map());
  });

  allSeries.forEach((series, seriesIndex) => {
    (series.data || []).forEach(point => {
      if (point.date && dateValueMap.has(point.date)) {
        dateValueMap.get(point.date).set(series.metricId, point.value || 0);
      }
    });
  });
  return (
    <div className={tableContainerClass}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={tableHeaderRowClass}>
              <th className={tableHeaderCellClass}>
                Metric
              </th>
              <th className={`${tableHeaderCellClass} text-right`}>
                Average
              </th>
              {dates.map((date, index) => (
                <th
                  key={index}
                  className={`${tableHeaderCellClass} text-right min-w-[72px]`}
                >
                  {formatDateForTable(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allSeries.map((series, seriesIndex) => {
              const avg = series.summary?.average ?? series.summary?.avg ?? 0;
              const color = getSeriesColor(seriesIndex, seriesColors || CHART_COLORS);
              
              return (
                <tr
                  key={`${series.metricId ?? 'series'}-${seriesIndex}`}
                  className={tableStaticRowClass}
                >
                  <td className={tableDataCellClass}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded"
                        style={{ backgroundColor: color }}
                      />
                      <span>{series.metricName || `Metric ${String.fromCharCode(65 + seriesIndex)}`}</span>
                    </div>
                  </td>
                  <td className={`${tableDataCellClass} text-right tabular-nums`}>
                    {formatNumber(avg, 1)}
                  </td>
                  {dates.map((date, dateIndex) => {
                    const value = dateValueMap.get(date)?.get(series.metricId) || 0;
                    return (
                      <td key={dateIndex} className={`${tableDataCellClass} text-right tabular-nums`}>
                        {formatNumber(value)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Grouped flat table for chartType === "table".
 * Columns: [Breakdown Columns...] → [Metric Columns...]
 * One row per deepest breakdown combination; no tree nesting.
 * Uses rowSpan to merge identical consecutive breakdown values.
 * Overall row at bottom.
 */
function GroupedFlatTable({ allSeries, seriesColors, breakdownProperties, fullTotalsByMetric }) {
  const numDims = Math.max(
    ...allSeries.map((s) => (s.breakdownValues?.length ?? 0)),
    1
  );
  const dimLabels = breakdownProperties?.length
    ? breakdownProperties.slice(0, numDims).map((p) => p.propertyLabel || p.property || "Dimension")
    : Array.from({ length: numDims }, (_, i) => `Dim ${i + 1}`);

  // Unique metrics (by metricId)
  const metricMap = new Map();
  allSeries.forEach((s, idx) => {
    const id = s.metricId ?? s.metricName ?? `m${idx}`;
    if (!metricMap.has(id)) {
      metricMap.set(id, {
        metricId: id,
        metricName: s.fullMetricName ?? s.metricName ?? `Metric`,
        color: getSeriesColor(metricMap.size, seriesColors || CHART_COLORS),
      });
    }
  });
  const metrics = Array.from(metricMap.values());

  // Build lookup: breakdownKey -> { metricId -> total }
  const breakdownKey = (vals) =>
    (vals || []).map((v) => String(v ?? "")).join("\x00");
  const rowMap = new Map();
  allSeries.forEach((s) => {
    const vals = s.breakdownValues ?? (s.breakdownKey ? [s.breakdownKey] : []);
    const padded = [...vals];
    while (padded.length < numDims) padded.push("(none)");
    const key = breakdownKey(padded);
    if (!rowMap.has(key)) rowMap.set(key, { dimValues: padded, values: new Map() });
    const entry = rowMap.get(key);
    const mid = s.metricId ?? s.metricName ?? "m";
    const total = s.summary?.total ?? (s.data || []).reduce((sum, p) => sum + (p.value ?? 0), 0);
    entry.values.set(mid, total);
  });

  // Flat rows sorted by breakdown
  const rows = Array.from(rowMap.values())
    .sort((a, b) => {
      for (let d = 0; d < numDims; d++) {
        const va = String(a.dimValues[d] ?? "");
        const vb = String(b.dimValues[d] ?? "");
        const c = va.localeCompare(vb);
        if (c !== 0) return c;
      }
      return 0;
    });

  // Overall totals: use fullTotalsByMetric (API totals) when provided, else sum displayed rows
  const overallTotals = new Map();
  metrics.forEach((m) => {
    const fullTotal = fullTotalsByMetric && (fullTotalsByMetric[m.metricId] ?? fullTotalsByMetric[m.metricName]) != null
      ? fullTotalsByMetric[m.metricId] ?? fullTotalsByMetric[m.metricName]
      : null;
    overallTotals.set(m.metricId, fullTotal != null ? fullTotal : 0);
  });
  if (!fullTotalsByMetric) {
    rows.forEach((r) => {
      metrics.forEach((m) => {
        const v = r.values.get(m.metricId) ?? 0;
        overallTotals.set(m.metricId, (overallTotals.get(m.metricId) ?? 0) + v);
      });
    });
  }

  // Row-span for breakdown columns: merge consecutive rows with same value for dim (and all parent dims)
  const getRowSpan = (rowIdx, dimIdx) => {
    if (rowIdx >= rows.length) return 0;
    const row = rows[rowIdx];
    let span = 1;
    for (let r = rowIdx + 1; r < rows.length; r++) {
      const curr = rows[r];
      for (let d = 0; d <= dimIdx; d++) {
        if (String(curr.dimValues[d] ?? "") !== String(row.dimValues[d] ?? "")) return span;
      }
      span++;
    }
    return span;
  };

  const shouldRenderCell = (rowIdx, dimIdx) => {
    if (rowIdx === 0) return true;
    const row = rows[rowIdx];
    const prev = rows[rowIdx - 1];
    for (let d = 0; d <= dimIdx; d++) {
      if (String(row.dimValues[d] ?? "") !== String(prev.dimValues[d] ?? "")) return true;
    }
    return false;
  };

  return (
    <div className={tableContainerClass}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={tableHeaderRowClass}>
              {dimLabels.map((label, i) => (
                <th key={i} className={tableHeaderCellClass}>
                  {label}
                </th>
              ))}
              {metrics.map((m) => (
                <th
                  key={m.metricId}
                  className={`${tableHeaderCellClass} text-right min-w-[72px]`}
                >
                  {m.metricName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={tableStaticRowClass}>
                {dimLabels.map((_, dimIdx) =>
                  shouldRenderCell(rowIdx, dimIdx) ? (
                    <td
                      key={dimIdx}
                      rowSpan={getRowSpan(rowIdx, dimIdx)}
                      className={tableDataCellClass}
                      style={{ verticalAlign: "top", paddingTop: 8 }}
                    >
                      {row.dimValues[dimIdx] ?? "(none)"}
                    </td>
                  ) : null
                )}
                {metrics.map((m) => {
                  const val = row.values.get(m.metricId);
                  const displayVal = val != null ? formatNumber(val) : "–";
                  return (
                    <td key={m.metricId} className={`${tableDataCellClass} text-right tabular-nums`}>
                      {displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Overall row */}
            <tr className={tableStaticRowClass}>
              <td colSpan={numDims} className={`${tableDataCellClass} font-medium`}>
                Overall
              </td>
              {metrics.map((m) => (
                <td key={m.metricId} className={`${tableDataCellClass} text-right tabular-nums font-medium`}>
                  {formatNumber(overallTotals.get(m.metricId) ?? 0)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Hierarchical table with row-span for identical consecutive dimension values.
 * Columns: Metrics | [Breakdown dims from breakdownProperties] | Average | [Date columns]
 * - Each metric block renders independently; rowSpan resets per metric.
 * - Within each metric: sort by breakdown hierarchy, then apply rowSpan on that block only.
 * - No cross-metric merging.
 */
function HierarchicalTable({ allSeries, seriesColors, breakdownProperties }) {
  const dateSet = new Set();
  allSeries.forEach((s) => {
    (s.data || []).forEach((p) => { if (p?.date) dateSet.add(p.date); });
  });
  const dates = Array.from(dateSet).sort((a, b) => new Date(a) - new Date(b));

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return `${d.toLocaleDateString("en-US", { month: "short" })} ${d.getDate()}`;
    } catch (e) {
      return String(dateStr).slice(-5);
    }
  };

  const numDims = Math.max(
    ...allSeries.map((s) => (s.breakdownValues?.length ?? 0)),
    1
  );
  const dimLabels = breakdownProperties?.length
    ? breakdownProperties.slice(0, numDims).map((p) => p.propertyLabel || p.property || "Dimension")
    : Array.from({ length: numDims }, (_, i) => `Dim ${i + 1}`);

  // Raw row builder
  const toRow = (s, idx, color) => {
    const dimVals = s.breakdownValues ?? (s.breakdownKey ? [s.breakdownKey] : []);
    const padded = [...dimVals];
    while (padded.length < numDims) padded.push("(none)");
    const avg = s.summary?.average ?? s.summary?.avg ?? 0;
    const dataByDate = new Map((s.data || []).map((p) => [p.date, p.value ?? 0]));
    return {
      key: `${s.metricId ?? "m"}-${idx}`,
      metricId: s.metricId,
      metricName: s.fullMetricName ?? s.metricName,
      color,
      dimValues: padded,
      average: avg,
      dateValues: dates.map((d) => dataByDate.get(d) ?? 0),
    };
  };

  // Step 1: Group by metric (each metric = one block)
  const metricGroups = new Map();
  allSeries.forEach((s, idx) => {
    const mid = s.metricId ?? s.metricName ?? `m${idx}`;
    if (!metricGroups.has(mid)) {
      metricGroups.set(mid, {
        metricId: mid,
        metricName: s.fullMetricName ?? s.metricName ?? "Metric",
        color: getSeriesColor(metricGroups.size, seriesColors || CHART_COLORS),
        rows: [],
      });
    }
    metricGroups.get(mid).rows.push(toRow(s, idx, metricGroups.get(mid).color));
  });

  // Step 2: For each metric block, sort rows by breakdown hierarchy (Country, City, Browser...)
  const metricBlocks = Array.from(metricGroups.values()).map((block) => ({
    ...block,
    rows: block.rows.sort((a, b) => {
      for (let d = 0; d < numDims; d++) {
        const va = String(a.dimValues[d] ?? "");
        const vb = String(b.dimValues[d] ?? "");
        const c = va.localeCompare(vb);
        if (c !== 0) return c;
      }
      return 0;
    }),
  }));

  // Step 3: RowSpan logic PER METRIC BLOCK (reset for each block)
  const getRowSpanInBlock = (blockRows, localRowIdx, dimIdx) => {
    if (localRowIdx >= blockRows.length) return 0;
    const row = blockRows[localRowIdx];
    const val = dimIdx === 0 ? row.metricId : row.dimValues[dimIdx - 1];
    let span = 1;
    for (let r = localRowIdx + 1; r < blockRows.length; r++) {
      const curr = blockRows[r];
      for (let d = 0; d <= dimIdx; d++) {
        const cv = d === 0 ? curr.metricId : curr.dimValues[d - 1];
        const rv = d === 0 ? row.metricId : row.dimValues[d - 1];
        if (String(cv ?? "") !== String(rv ?? "")) return span;
      }
      span++;
    }
    return span;
  };

  const shouldRenderCellInBlock = (blockRows, localRowIdx, dimIdx) => {
    if (localRowIdx === 0) return true;
    const row = blockRows[localRowIdx];
    const prev = blockRows[localRowIdx - 1];
    for (let d = 0; d <= dimIdx; d++) {
      const v = d === 0 ? row.metricId : row.dimValues[d - 1];
      const pv = d === 0 ? prev.metricId : prev.dimValues[d - 1];
      if (String(v ?? "") !== String(pv ?? "")) return true;
    }
    return false;
  };
  const METRIC_COL_WIDTH = 200;
  const DIM_COL_WIDTH = 120;

  return (
    <div className={tableContainerClass}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <colgroup>
            <col style={{ minWidth: METRIC_COL_WIDTH }} />
            {dimLabels.map((_, i) => <col key={i} style={{ minWidth: DIM_COL_WIDTH }} />)}
            <col style={{ minWidth: 72 }} />
          </colgroup>
          <thead>
            <tr className={tableHeaderRowClass}>
              <th className={tableHeaderCellClass}>
                Metrics
              </th>
              {dimLabels.map((label, i) => (
                <th key={i} className={tableHeaderCellClass}>
                  {label}
                </th>
              ))}
              <th className={`${tableHeaderCellClass} text-right`}>
                Average
              </th>
              {dates.map((d, i) => (
                <th key={i} className={`${tableHeaderCellClass} text-right min-w-[72px]`}>
                  {formatDate(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricBlocks.map((block) =>
              block.rows.map((row, localRowIdx) => (
                <tr key={row.key} className={tableStaticRowClass}>
                  {/* Metric column: single cell per block spanning all rows */}
                  {localRowIdx === 0 && (
                    <td
                      rowSpan={block.rows.length}
                      className={tableDataCellClass}
                      style={{ verticalAlign: "top", paddingTop: 8 }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 w-2.5 shrink-0 rounded" style={{ backgroundColor: row.color }} />
                        <span>{row.metricName || "Metric"}</span>
                      </div>
                    </td>
                  )}
                  {/* Breakdown dimension columns: rowSpan within this metric block only */}
                  {dimLabels.map((_, dimIdx) =>
                    shouldRenderCellInBlock(block.rows, localRowIdx, dimIdx + 1) ? (
                      <td
                        key={dimIdx}
                        rowSpan={getRowSpanInBlock(block.rows, localRowIdx, dimIdx + 1)}
                        className={tableDataCellClass}
                        style={{ verticalAlign: "top", paddingTop: 8 }}
                      >
                        {row.dimValues[dimIdx] ?? "(none)"}
                      </td>
                    ) : null
                  )}
                  {/* Average */}
                  <td className={`${tableDataCellClass} text-right tabular-nums`}>
                    {formatNumber(row.average, 1)}
                  </td>
                  {/* Date columns */}
                  {row.dateValues.map((val, dateIdx) => (
                    <td key={dateIdx} className={`${tableDataCellClass} text-right tabular-nums`}>
                      {formatNumber(val)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
